import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/status-badge";
import { formatDateTime } from "@/lib/format";
import { sanitizeEmailHtml } from "@/lib/sanitize-email";
import { emailApi, type EntityKind, type ThreadMessage } from "@/lib/email";
import { usePermissions } from "@/hooks/use-permissions";
import { ComposeEmailDialog } from "./ComposeEmailDialog";

export interface EmailThreadTabProps {
  entityType: EntityKind;
  entityId: number;
  defaultTo?: string;
}

export function EmailThreadTab({ entityType, entityId, defaultTo }: EmailThreadTabProps) {
  const { has } = usePermissions();
  const [composeOpen, setComposeOpen] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["email-thread", entityType, entityId],
    queryFn: () => emailApi.thread(entityType, entityId),
  });

  const compose = has("email:compose") ? (
    <>
      <Button size="sm" onClick={() => setComposeOpen(true)}>
        Compose
      </Button>
      <ComposeEmailDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        entityType={entityType}
        entityId={entityId}
        defaultTo={defaultTo}
        onSent={() => refetch()}
      />
    </>
  ) : null;

  if (isLoading) return <p className="p-4 text-sm text-muted-foreground">Loading emails…</p>;

  if (isError)
    return (
      <div role="alert" className="p-4 text-sm text-destructive">
        Failed to load emails.{" "}
        <button type="button" className="underline" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );

  if (!data || data.length === 0)
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-10 text-center text-sm text-muted-foreground">
          No emails on this entity yet.
          {compose}
        </CardContent>
      </Card>
    );

  return (
    <div className="space-y-3">
      <div className="flex justify-end">{compose}</div>
      {data.map((m) => (
        <ThreadCard key={`${m.source}-${m.sourceId}`} message={m} />
      ))}
    </div>
  );
}

function ThreadCard({ message }: { message: ThreadMessage }) {
  const [expanded, setExpanded] = useState(false);
  const [showRemote, setShowRemote] = useState(false);
  const inbound = message.direction === "Inbound";
  const body = sanitizeEmailHtml(message.bodyHtml ?? "", { allowRemoteImages: showRemote });

  return (
    <Card>
      <CardContent className="p-4">
        <button
          type="button"
          className="flex w-full items-start justify-between gap-3 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          <div>
            <div className="flex items-center gap-2 text-sm font-medium">
              <span aria-label={message.direction}>{inbound ? "↙" : "↗"}</span>
              <span>{message.subject}</span>
              {message.status && (
                <StatusBadge tone={message.status === "Sent" ? "success" : "danger"}>
                  {message.status}
                </StatusBadge>
              )}
              {message.matchConfidence === 1 && <StatusBadge tone="warning">Low-confidence match</StatusBadge>}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {message.from} → {message.to} · {formatDateTime(message.timestampUtc)}
            </div>
          </div>
        </button>

        {expanded && (
          <div className="mt-3 border-t border-border pt-3">
            {inbound && (
              <button
                type="button"
                className="mb-2 text-xs underline text-muted-foreground"
                onClick={() => setShowRemote((v) => !v)}
              >
                {showRemote ? "Hide external content" : "Show external content"}
              </button>
            )}
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: body }} />
            {message.attachments.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-2">
                {message.attachments.map((a, i) => (
                  <li key={i} className="rounded border border-border px-2 py-1 text-xs">
                    {a.downloadUrl ? (
                      <a className="underline" href={a.downloadUrl}>
                        {a.fileName}
                      </a>
                    ) : (
                      a.fileName
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
